import React, { useMemo, useState } from 'react'
import { orderBy } from 'lodash'
import { NODE_TYPE } from '../components/CatalogTaxonomy/catalogTaxonomyConstants'
import { computeEffectiveState } from '../components/CatalogTaxonomy/catalogTaxonomyEffectiveState'
import { isCatalogTaxonomyLeafNode } from '../components/CatalogTaxonomy/catalogTaxonomyLeaf'
import { resolveAncestors, assertCanAssignTaxonomyNode } from './productTaxonomyFields'
import './CatalogTaxonomyNodePicker.sass'

function getChildren(nodes, parentId) {
  const list = (nodes || []).filter((node) =>
    parentId == null ? node.parentId == null : node.parentId === parentId
  )
  return orderBy(list, ['sortOrder', 'name'], ['asc', 'asc'])
}

function NodeColumn({
  title,
  nodes,
  selectedId,
  assignedIds,
  nodesById,
  onSelect,
  onAssign,
  mode,
  selectAnyNode = false,
}) {
  return (
    <div className="CatalogTaxonomyNodePicker-Column">
      <div className="CatalogTaxonomyNodePicker-ColumnTitle">{title}</div>
      <div className="CatalogTaxonomyNodePicker-List">
        {nodes.length === 0 ? (
          <p className="CatalogTaxonomyNodePicker-Empty">Немає елементів</p>
        ) : (
          nodes.map((node) => {
            const ancestors = resolveAncestors(node.id, nodesById)
            const { effectiveArchived } = computeEffectiveState(node, ancestors)
            const isLeaf = isCatalogTaxonomyLeafNode(node.id, nodesById)
            const assignGuard = assertCanAssignTaxonomyNode(node, ancestors, nodesById)
            const isAssigned = assignedIds.includes(node.id)
            const isSelected = selectedId === node.id
            const pickAllowed =
              mode === 'select'
                ? selectAnyNode || isLeaf
                : assignGuard.allowed
            const showAssign = isLeaf || (mode === 'select' && selectAnyNode)
            const className = [
              'CatalogTaxonomyNodePicker-Item',
              isSelected ? 'CatalogTaxonomyNodePicker-Item_selected' : '',
              isAssigned ? 'CatalogTaxonomyNodePicker-Item_assigned' : '',
              effectiveArchived && mode !== 'select' ? 'CatalogTaxonomyNodePicker-Item_blocked' : '',
              !isLeaf ? 'CatalogTaxonomyNodePicker-Item_parent' : '',
            ]
              .filter(Boolean)
              .join(' ')

            return (
              <div key={node.id} className={className}>
                <button
                  type="button"
                  className="CatalogTaxonomyNodePicker-ItemSelect"
                  onClick={() => {
                    if (isLeaf) {
                      if (mode === 'select') {
                        onAssign(node, assignGuard)
                        return
                      }
                      if (!pickAllowed || isAssigned) return
                      onAssign(node, assignGuard)
                      return
                    }
                    onSelect(node)
                  }}
                  title={
                    isLeaf
                      ? mode === 'select'
                        ? isAssigned
                          ? 'Зняти вибір'
                          : 'Обрати'
                        : isAssigned
                          ? 'Вже обрано'
                          : assignGuard.message || 'Призначити'
                      : 'Перейдіть до дочірньої категорії'
                  }
                >
                  <span className="CatalogTaxonomyNodePicker-ItemRow">
                    <span className="CatalogTaxonomyNodePicker-ItemName">{node.name || '—'}</span>
                    {!isLeaf ? (
                      <span
                        className={[
                          'CatalogTaxonomyNodePicker-ItemHint',
                          'CatalogTaxonomyNodePicker-ItemHint_nav',
                          isSelected ? 'CatalogTaxonomyNodePicker-ItemHint_navActive' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        →
                      </span>
                    ) : null}
                  </span>
                  {isLeaf && assignGuard.warning ? (
                    <span className="CatalogTaxonomyNodePicker-ItemHint">неактивна</span>
                  ) : null}
                  {isLeaf && effectiveArchived ? (
                    <span className="CatalogTaxonomyNodePicker-ItemHint">архів</span>
                  ) : null}
                </button>
                {showAssign ? (
                  <button
                    type="button"
                    className={[
                      'CatalogTaxonomyNodePicker-Assign',
                      isAssigned ? 'CatalogTaxonomyNodePicker-Assign_checked' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={mode === 'select' ? false : !pickAllowed || isAssigned}
                    title={
                      isAssigned
                        ? mode === 'select'
                          ? 'Зняти вибір'
                          : 'Вже обрано'
                        : mode === 'select'
                          ? 'Обрати'
                          : assignGuard.message || 'Призначити'
                    }
                    onClick={() => onAssign(node, assignGuard)}
                  >
                    {isAssigned ? '✓' : '+'}
                  </button>
                ) : null}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

/** Open Miller columns on the path of an existing leaf (global + mid category). */
function getInitialColumnFocus(focusNodeId, nodesById) {
  if (!focusNodeId || !nodesById?.[focusNodeId]) {
    return { globalId: '', categoryId: '' }
  }
  const ancestors = resolveAncestors(focusNodeId, nodesById)
  return {
    globalId: ancestors[0]?.id || '',
    // Depth Global→Leaf: leave mid empty so leaf shows in column 2.
    // Depth Global→Category→Leaf: open parent so leaf shows in column 3.
    categoryId: ancestors.length >= 2 ? ancestors[1].id : '',
  }
}

/**
 * 3-column taxonomy picker: parent = navigate, leaf = assign (+).
 * mode: 'assign' (default) | 'select' (pick via onPick)
 * selectAnyNode: in select mode allow parent nodes (nested membership).
 * focusNodeId: optional existing leaf — pre-open its path in the columns
 */
function CatalogTaxonomyNodePicker({
  nodes = [],
  nodesById = {},
  assignedIds = [],
  mode = 'assign',
  hint = 'Оберіть кінцеву категорію',
  focusNodeId = null,
  selectAnyNode = false,
  onAssign,
  onPick,
}) {
  const initialFocus = getInitialColumnFocus(focusNodeId, nodesById)
  const [globalId, setGlobalId] = useState(initialFocus.globalId)
  const [categoryId, setCategoryId] = useState(initialFocus.categoryId)

  const globals = useMemo(
    () => getChildren(nodes, null).filter((n) => n.type === NODE_TYPE.GLOBAL || !n.type),
    [nodes]
  )
  const categories = useMemo(
    () => (globalId ? getChildren(nodes, globalId) : []),
    [nodes, globalId]
  )
  const subcategories = useMemo(
    () => (categoryId ? getChildren(nodes, categoryId) : []),
    [nodes, categoryId]
  )

  const handleAssign = (node, guard) => {
    if (mode === 'select') {
      if (!selectAnyNode && !isCatalogTaxonomyLeafNode(node.id, nodesById)) return
      if (onPick) onPick(node, guard)
      return
    }
    if (!guard.allowed) {
      window.alert(guard.message || 'Не можна призначити цей вузол.')
      return
    }
    if (onAssign) onAssign(node, guard)
  }

  return (
    <div className="CatalogTaxonomyNodePicker">
      {hint ? <p className="CatalogTaxonomyNodePicker-Hint">{hint}</p> : null}
      <NodeColumn
        title="Глобальні"
        nodes={globals}
        selectedId={globalId}
        assignedIds={assignedIds}
        nodesById={nodesById}
        mode={mode}
        selectAnyNode={selectAnyNode}
        onSelect={(node) => {
          setGlobalId(node.id)
          setCategoryId('')
        }}
        onAssign={handleAssign}
      />
      <NodeColumn
        title="Категорії"
        nodes={categories}
        selectedId={categoryId}
        assignedIds={assignedIds}
        nodesById={nodesById}
        mode={mode}
        selectAnyNode={selectAnyNode}
        onSelect={(node) => setCategoryId(node.id)}
        onAssign={handleAssign}
      />
      <NodeColumn
        title="Підкатегорії"
        nodes={subcategories}
        selectedId=""
        assignedIds={assignedIds}
        nodesById={nodesById}
        mode={mode}
        selectAnyNode={selectAnyNode}
        onSelect={() => {}}
        onAssign={handleAssign}
      />
    </div>
  )
}

export default CatalogTaxonomyNodePicker
